/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import {
  DropdownOption,
  useAustraliaAcbRatingsList,
  useBrazilRatingsList,
  useEsrbRatingsList,
  useEuropeIsfeRatingsList,
  useGermanyUskRatingsList,
  useJapanCeroRatingsList,
  useKoreaGrbRatingsList,
  useNewZealandOflcRatingsList,
  useRussiaPcbpRatingsList,
  useSaudiArabiaGamrRatingsList,
  useTaiwanCsrrRatingsList,
} from "../../../../helpers/optionSets";
import { AaaGameInformationAvailabilityDataType } from "../../../../models/forms";
import {
  LanguageItem,
  useLanguagesList,
} from "../../../GameInformationPage/PricingAndAvailabilitySubForm/languagesList";
import { ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";
import { SensitiveContentStatic } from "../../../../server/crm/dataService/models/staticOptions";

type ReadOnlyAaaAvailabilityProps = {
  dataInForm: AaaGameInformationAvailabilityDataType;
};

const mapRatingsNameToTranslatedStringKey = (input: string): string => {
  const mappings: { [key: string]: string } = {
    esrb: "aaaEsrbRatingDropdownTitle",
    europeIsfe: "aaaEuropeIsfeRatingDropdownTitle",
    japanCero: "aaaJapanCeroRatingDropdownTitle",
    brazilRatings: "aaaBrazilRatingDropdownTitle",
    germanyUsk: "aaaGermanyUskRatingDropdownTitle",
    australiaAcb: "aaaAustraliaAcbRatingDropdownTitle",
    koreaGrb: "aaaKoreaGracRatingDropdownTitle",
    newZealandOflc: "aaaNewZealandOflcRatingDropdownTitle",
    taiwanCsrr: "aaaTaiwanCsrrRatingDropdownTitle",
    russiaPcbp: "aaaRussiaPcbpRatingDropdownTitle",
    saudiArabiaGamr: "aaaSaudiArabiaGamrRatingDropdownTitle",
  };

  return mappings[input] || "";
};

const mapRatingsList = (ratingsKey: string, additionalData: Map<string, any>) => {
  switch (ratingsKey) {
    case "esrb":
      return additionalData.get("esrbRatingsList");
    case "europeIsfe":
      return additionalData.get("europeIsfeRatingsList");
    case "japanCero":
      return additionalData.get("japanCeroRatingsList");
    case "brazilRatings":
      return additionalData.get("brazilRatingsList");
    case "germanyUsk":
      return additionalData.get("germanyUskRatingsList");
    case "australiaAcb":
      return additionalData.get("australiaAcbRatingsList");
    case "koreaGrb":
      return additionalData.get("koreaGrbRatingsList");
    case "newZealandOflc":
      return additionalData.get("newZealandOflcRatingsList");
    case "taiwanCsrr":
      return additionalData.get("taiwanCsrrRatingsList");
    case "russiaPcbp":
      return additionalData.get("russiaPcbpRatingsList");
    case "saudiArabiaGamr":
      return additionalData.get("saudiArabiaGamrRatingsList");
  }
  return null;
};

const AaaAvailabilityConfig: Map<string, ReadOnlyFormItem> = new Map([
  [
    "supportedLanguages",
    {
      mapper: (
        data: AaaGameInformationAvailabilityDataType,
        _localizedStrings: Map<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalData?: Map<string, any>
      ) => {
        if (!additionalData) {
          return null;
        }
        const languagesList = additionalData.get("languagesList") as LanguageItem[];
        return data.supportedLanguages?.map(
          language => languagesList.find(item => item.value === language)?.label
        );
      },
    },
  ],
  ["otherLanguages", {}],
  [
    "anticipatedRatings",
    {
      title: "aaaAvailabilityAnticipatedRatingsTitle",
      mapper: (
        data: AaaGameInformationAvailabilityDataType,
        localizedStrings: Map<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalData?: Map<string, any>
      ) =>
        additionalData
          ? Object.keys(data.anticipatedRatings).reduce((result: string[], ratingsKey) => {
              const ratingsRawValue = data.anticipatedRatings[ratingsKey];
              if (!ratingsRawValue) {
                return result;
              }

              const ratingsList = mapRatingsList(ratingsKey, additionalData) as DropdownOption[];
              const ratingsValue = ratingsList
                ? ratingsList.find(rating => rating.value === ratingsRawValue)?.label
                : undefined;

              if (ratingsValue) {
                const formattedRating = `${localizedStrings.get(
                  mapRatingsNameToTranslatedStringKey(ratingsKey)
                )}: ${ratingsValue}`;
                result.push(formattedRating);
              }

              return result;
            }, [])
          : null,
    },
  ],
  [
    "sensitiveContent",
    {
      title: "sensitiveContentReadOnlyTitle",
      mapper: (
        data: AaaGameInformationAvailabilityDataType,
        localizedStrings: Map<string, string>
      ) => {
        const sensitiveContent = data.sensitiveContent;
        let locKey = "other";
        switch (sensitiveContent) {
          case SensitiveContentStatic.Yes.toString():
            locKey = "yes";
            break;
          case SensitiveContentStatic.No.toString():
            locKey = "no";
            break;
          case SensitiveContentStatic.Maybe.toString():
            locKey = "maybe";
            break;
          case SensitiveContentStatic.Other.toString():
            locKey = "other";
            break;
        }
        return localizedStrings.get(locKey);
      },
    },
  ],
  [
    "sensitiveContentDetails",
    {
      title: "sensitiveContentDetailsReadOnlyTitle",
    },
  ],
]);

export const ReadOnlyAaaAvailability = ({ dataInForm }: ReadOnlyAaaAvailabilityProps) => {
  const extraStringsToLocalize = [
    "aaaEsrbRatingDropdownTitle",
    "aaaEuropeIsfeRatingDropdownTitle",
    "aaaJapanCeroRatingDropdownTitle",
    "aaaBrazilRatingDropdownTitle",
    "aaaGermanyUskRatingDropdownTitle",
    "aaaAustraliaAcbRatingDropdownTitle",
    "aaaKoreaGracRatingDropdownTitle",
    "aaaNewZealandOflcRatingDropdownTitle",
    "aaaTaiwanCsrrRatingDropdownTitle",
    "aaaRussiaPcbpRatingDropdownTitle",
    "aaaSaudiArabiaGamrRatingDropdownTitle",
    "yes",
    "no",
    "maybe",
    "other",
  ];

  const languagesList = useLanguagesList();
  const esrbRatingsList = useEsrbRatingsList();
  const germanyUskRatingsList = useGermanyUskRatingsList();
  const taiwanCsrrRatingsList = useTaiwanCsrrRatingsList();
  const europeIsfeRatingsList = useEuropeIsfeRatingsList();
  const australiaAcbRatingsList = useAustraliaAcbRatingsList();
  const russiaPcbpRatingsList = useRussiaPcbpRatingsList();
  const japanCeroRatingsList = useJapanCeroRatingsList();
  const koreaGrbRatingsList = useKoreaGrbRatingsList();
  const saudiArabiaGamrRatingsList = useSaudiArabiaGamrRatingsList();
  const brazilRatingsList = useBrazilRatingsList();
  const newZealandOflcRatingsList = useNewZealandOflcRatingsList();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const additionalData = new Map<string, any>([
    ["languagesList", languagesList],
    ["esrbRatingsList", esrbRatingsList],
    ["germanyUskRatingsList", germanyUskRatingsList],
    ["taiwanCsrrRatingsList", taiwanCsrrRatingsList],
    ["europeIsfeRatingsList", europeIsfeRatingsList],
    ["australiaAcbRatingsList", australiaAcbRatingsList],
    ["russiaPcbpRatingsList", russiaPcbpRatingsList],
    ["japanCeroRatingsList", japanCeroRatingsList],
    ["koreaGrbRatingsList", koreaGrbRatingsList],
    ["saudiArabiaGamrRatingsList", saudiArabiaGamrRatingsList],
    ["brazilRatingsList", brazilRatingsList],
    ["newZealandOflcRatingsList", newZealandOflcRatingsList],
  ]);

  return (
    <ReadOnlyForm
      formConfig={AaaAvailabilityConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
      additionalData={additionalData}
    />
  );
};
